/**
 * Template Management API Hooks
 * React Query hooks for Standards, Departments, Touchpoints, SubTouchpoints, Titles CRUD operations
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import AxiosClient from '@/api/client';
import { Endpoints, EndpointHelpers } from '@/config/endpoints';
import { DEFAULT_PAGE_SIZE } from '@/config/constants';
import { toast } from '@/hooks/use-toast';
import type {
  ApiStandard,
  ApiStandardDetail,
  UpdateStandardRequest,
  StandardsListParams,
  ApiDepartment,
  ApiDepartmentDetail,
  CreateDepartmentRequest,
  UpdateDepartmentRequest,
  DepartmentsListParams,
  ApiTouchpoint,
  ApiTouchpointDetail,
  CreateTouchpointRequest,
  UpdateTouchpointRequest,
  TouchpointsListParams,
  ApiSubTouchpoint,
  ApiSubTouchpointDetail,
  CreateSubTouchpointRequest,
  UpdateSubTouchpointRequest,
  SubTouchpointsListParams,
  ApiTitle,
  ApiTitleDetail,
  CreateTitleRequest,
  UpdateTitleRequest,
  TitlesListParams,
  BatchCreateStandardsRequest,
  BatchCreateStandardsResponse,
} from '@/types/templates';
import type { ItemResponse, ListPaginated } from '@/types/api';

// ============================================================================
// Query Keys
// ============================================================================

export const standardQueryKeys = {
  all: ['standards'] as const,
  lists: () => [...standardQueryKeys.all, 'list'] as const,
  list: (propertyTypeId: string | number, params: StandardsListParams) =>
    [...standardQueryKeys.lists(), propertyTypeId, params] as const,
  details: () => [...standardQueryKeys.all, 'detail'] as const,
  detail: (id: string | number) =>
    [...standardQueryKeys.details(), id] as const,
} as const;

export const departmentQueryKeys = {
  all: ['departments'] as const,
  lists: () => [...departmentQueryKeys.all, 'list'] as const,
  list: (propertyTypeId: string | number, params: DepartmentsListParams) =>
    [...departmentQueryKeys.lists(), propertyTypeId, params] as const,
  details: () => [...departmentQueryKeys.all, 'detail'] as const,
  detail: (id: string | number) =>
    [...departmentQueryKeys.details(), id] as const,
} as const;

export const touchpointQueryKeys = {
  all: ['touchpoints'] as const,
  lists: () => [...touchpointQueryKeys.all, 'list'] as const,
  list: (propertyTypeId: string | number, params: TouchpointsListParams) =>
    [...touchpointQueryKeys.lists(), propertyTypeId, params] as const,
  details: () => [...touchpointQueryKeys.all, 'detail'] as const,
  detail: (id: string | number) =>
    [...touchpointQueryKeys.details(), id] as const,
} as const;

export const subTouchpointQueryKeys = {
  all: ['subTouchpoints'] as const,
  lists: () => [...subTouchpointQueryKeys.all, 'list'] as const,
  list: (propertyTypeId: string | number, params: SubTouchpointsListParams) =>
    [...subTouchpointQueryKeys.lists(), propertyTypeId, params] as const,
  details: () => [...subTouchpointQueryKeys.all, 'detail'] as const,
  detail: (id: string | number) =>
    [...subTouchpointQueryKeys.details(), id] as const,
} as const;

export const titleQueryKeys = {
  all: ['titles'] as const,
  lists: () => [...titleQueryKeys.all, 'list'] as const,
  list: (propertyTypeId: string | number, params: TitlesListParams) =>
    [...titleQueryKeys.lists(), propertyTypeId, params] as const,
  details: () => [...titleQueryKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...titleQueryKeys.details(), id] as const,
} as const;

// ============================================================================
// Standards Queries & Mutations
// ============================================================================

export function useStandardsQuery(
  propertyTypeId: string | number,
  params: StandardsListParams = {}
) {
  return useQuery({
    queryKey: standardQueryKeys.list(propertyTypeId, params),
    queryFn: async (): Promise<ListPaginated<ApiStandard>> => {
      const client = new AxiosClient<undefined, ApiStandard>({
        endpoint: EndpointHelpers.getStandardsList(propertyTypeId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getPaginated({
        params: {
          page: params.page || 1,
          page_size: params.page_size || DEFAULT_PAGE_SIZE,
          ...(params.search && { search: params.search }),
          ...(params.department_id && { department_id: params.department_id }),
          ...(params.touchpoint_id && { touchpoint_id: params.touchpoint_id }),
          ...(params.sub_touchpoint_id && {
            sub_touchpoint_id: params.sub_touchpoint_id,
          }),
          ...(params.title_id && { title_id: params.title_id }),
          ...(params.sort_by && { sort_by: params.sort_by }),
          ...(params.sort_order && { sort_order: params.sort_order }),
        },
      });

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch standards');
      }

      return response;
    },
    enabled: !!propertyTypeId,
    staleTime: 0, // Always consider data stale - fetch on every mount/param change
    gcTime: 5 * 60 * 1000, // Keep unused data in cache for 5 minutes
    refetchOnMount: true, // Refetch when component mounts
    placeholderData: previousData => previousData, // Keep previous data while fetching new
  });
}

export function useStandardQuery(
  standardId: string | number,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: standardQueryKeys.detail(standardId),
    queryFn: async (): Promise<ApiStandardDetail> => {
      const client = new AxiosClient<undefined, ApiStandardDetail>({
        endpoint: EndpointHelpers.getStandardDetail(standardId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getItem();

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch standard details');
      }

      return response.data;
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!standardId,
    staleTime: 5 * 60 * 1000,
  });
}

interface UseCreateStandardMutationOptions {
  onSuccessCallback?: () => void;
}

export function useBatchCreateStandardsMutation(
  options: UseCreateStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      batchData: BatchCreateStandardsRequest
    ): Promise<BatchCreateStandardsResponse> => {
      const client = new AxiosClient<
        BatchCreateStandardsRequest,
        BatchCreateStandardsResponse
      >({
        endpoint: '/standards/batch',
        contentType: 'application/json',
        isToken: true,
      });

      // AxiosClient.post returns ItemResponse<BatchCreateStandardsResponse>
      // But BatchCreateStandardsResponse is already the full response structure
      // So we need to extract it from ItemResponse wrapper
      const response = await client.post(batchData);

      // The actual batch response is in response.data
      if (!response.status || !response.data) {
        throw new Error(response.error || 'Failed to create standards');
      }

      return response.data;
    },

    onSuccess: data => {
      const { summary } = data;

      toast({
        title: 'Standards Created',
        description: `${summary.success} standards created successfully${summary.failed > 0 ? `, ${summary.failed} failed` : ''}.`,
        variant: summary.failed > 0 ? 'default' : 'default',
      });

      // Invalidate standards queries to refetch standards list
      queryClient.invalidateQueries({ queryKey: standardQueryKeys.all });

      // Invalidate property types queries to update standards_count
      queryClient.invalidateQueries({ queryKey: ['propertyTypes'] });

      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Batch create standards error:', error);

      let errorMessage = 'Failed to create standards. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        if (typeof errorData.detail === 'object') {
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join(', ');
        } else {
          errorMessage = errorData.error || errorData.detail || errorMessage;
        }
      }

      toast({
        title: 'Creation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

interface UseUpdateStandardMutationOptions {
  onSuccessCallback?: () => void;
}

export function useUpdateStandardMutation(
  standardId: string | number,
  options: UseUpdateStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      standardData: UpdateStandardRequest
    ): Promise<ItemResponse<ApiStandardDetail>> => {
      const client = new AxiosClient<UpdateStandardRequest, ApiStandardDetail>({
        endpoint: EndpointHelpers.updateStandard(standardId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.put(standardData);

      if (!response.status) {
        throw new Error(response.error || 'Failed to update standard');
      }

      return response;
    },

    onSuccess: () => {
      toast({
        title: 'Standard Updated',
        description: 'Standard has been successfully updated.',
      });

      queryClient.invalidateQueries({ queryKey: standardQueryKeys.all });
      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Update standard error:', error);

      let errorMessage = 'Failed to update standard. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        if (typeof errorData.detail === 'object') {
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join(', ');
        } else {
          errorMessage = errorData.error || errorData.detail || errorMessage;
        }
      }

      toast({
        title: 'Update Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

interface UseDeleteStandardMutationOptions {
  onSuccessCallback?: () => void;
}

export function useDeleteStandardMutation(
  options: UseDeleteStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      standardId: string | number
    ): Promise<ItemResponse<{ id: number; deleted_at: string }>> => {
      const client = new AxiosClient<
        undefined,
        { id: number; deleted_at: string }
      >({
        endpoint: EndpointHelpers.deleteStandard(standardId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.delete();

      if (!response.status) {
        throw new Error(response.error || 'Failed to delete standard');
      }

      return response;
    },

    onSuccess: (_, standardId) => {
      toast({
        title: 'Standard Deleted',
        description: 'Standard has been successfully deleted.',
      });

      // Invalidate standards queries to refetch standards list
      queryClient.invalidateQueries({ queryKey: standardQueryKeys.all });
      queryClient.removeQueries({
        queryKey: standardQueryKeys.detail(standardId),
      });

      // Invalidate property types queries to update standards_count
      queryClient.invalidateQueries({ queryKey: ['propertyTypes'] });

      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Delete standard error:', error);

      let errorMessage = 'Failed to delete standard. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;
        errorMessage = errorData.error || errorData.detail || errorMessage;
      }

      toast({
        title: 'Delete Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteDepartmentMutation(
  options: UseDeleteStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      departmentId: string | number
    ): Promise<ItemResponse<{ id: number; deleted_at: string }>> => {
      const client = new AxiosClient<
        undefined,
        { id: number; deleted_at: string }
      >({
        endpoint: EndpointHelpers.deleteDepartment(departmentId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.delete();

      if (!response.status) {
        throw new Error(response.error || 'Failed to delete department');
      }

      return response;
    },

    onSuccess: (_, departmentId) => {
      toast({
        title: 'Department Deleted',
        description: 'Department has been successfully deleted.',
      });

      queryClient.invalidateQueries({ queryKey: departmentQueryKeys.all });
      queryClient.removeQueries({
        queryKey: departmentQueryKeys.detail(departmentId),
      });
      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Delete department error:', error);

      let errorMessage = 'Failed to delete department. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;
        errorMessage = errorData.error || errorData.detail || errorMessage;
      }

      toast({
        title: 'Delete Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

export function useCreateDepartmentMutation(
  options: UseCreateStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      departmentData: CreateDepartmentRequest
    ): Promise<ItemResponse<ApiDepartmentDetail>> => {
      const client = new AxiosClient<
        CreateDepartmentRequest,
        ApiDepartmentDetail
      >({
        endpoint: Endpoints.DEPARTMENT_CREATE,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.post(departmentData);

      if (!response.status) {
        throw new Error(response.error || 'Failed to create department');
      }

      return response;
    },

    onSuccess: () => {
      toast({
        title: 'Department Created',
        description: 'Department has been successfully created.',
      });

      queryClient.invalidateQueries({ queryKey: departmentQueryKeys.all });
      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Create department error:', error);

      let errorMessage = 'Failed to create department. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        if (typeof errorData.detail === 'object') {
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join(', ');
        } else {
          errorMessage = errorData.error || errorData.detail || errorMessage;
        }
      }

      toast({
        title: 'Creation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

export function useCreateTouchpointMutation(
  options: UseCreateStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      touchpointData: CreateTouchpointRequest
    ): Promise<ItemResponse<ApiTouchpointDetail>> => {
      const client = new AxiosClient<
        CreateTouchpointRequest,
        ApiTouchpointDetail
      >({
        endpoint: Endpoints.TOUCHPOINT_CREATE,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.post(touchpointData);

      if (!response.status) {
        throw new Error(response.error || 'Failed to create touchpoint');
      }

      return response;
    },

    onSuccess: () => {
      toast({
        title: 'Touchpoint Created',
        description: 'Touchpoint has been successfully created.',
      });

      queryClient.invalidateQueries({ queryKey: touchpointQueryKeys.all });
      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Create touchpoint error:', error);

      let errorMessage = 'Failed to create touchpoint. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        if (typeof errorData.detail === 'object') {
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join(', ');
        } else {
          errorMessage = errorData.error || errorData.detail || errorMessage;
        }
      }

      toast({
        title: 'Creation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

export function useCreateSubTouchpointMutation(
  options: UseCreateStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      subTouchpointData: CreateSubTouchpointRequest
    ): Promise<ItemResponse<ApiSubTouchpointDetail>> => {
      const client = new AxiosClient<
        CreateSubTouchpointRequest,
        ApiSubTouchpointDetail
      >({
        endpoint: Endpoints.SUB_TOUCHPOINT_CREATE,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.post(subTouchpointData);

      if (!response.status) {
        throw new Error(response.error || 'Failed to create sub-touchpoint');
      }

      return response;
    },

    onSuccess: () => {
      toast({
        title: 'Sub-Touchpoint Created',
        description: 'Sub-touchpoint has been successfully created.',
      });

      queryClient.invalidateQueries({ queryKey: subTouchpointQueryKeys.all });
      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Create sub-touchpoint error:', error);

      let errorMessage = 'Failed to create sub-touchpoint. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        if (typeof errorData.detail === 'object') {
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join(', ');
        } else {
          errorMessage = errorData.error || errorData.detail || errorMessage;
        }
      }

      toast({
        title: 'Creation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

export function useCreateTitleMutation(
  options: UseCreateStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      titleData: CreateTitleRequest
    ): Promise<ItemResponse<ApiTitleDetail>> => {
      const client = new AxiosClient<CreateTitleRequest, ApiTitleDetail>({
        endpoint: Endpoints.TITLE_CREATE,
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.post(titleData);

      if (!response.status) {
        throw new Error(response.error || 'Failed to create title');
      }

      return response;
    },

    onSuccess: () => {
      toast({
        title: 'Title Created',
        description: 'Title has been successfully created.',
      });

      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all });
      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Create title error:', error);

      let errorMessage = 'Failed to create title. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        if (typeof errorData.detail === 'object') {
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join(', ');
        } else {
          errorMessage = errorData.error || errorData.detail || errorMessage;
        }
      }

      toast({
        title: 'Creation Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteTouchpointMutation(
  options: UseDeleteStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      touchpointId: string | number
    ): Promise<ItemResponse<{ id: number; deleted_at: string }>> => {
      const client = new AxiosClient<
        undefined,
        { id: number; deleted_at: string }
      >({
        endpoint: EndpointHelpers.deleteTouchpoint(touchpointId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.delete();

      if (!response.status) {
        throw new Error(response.error || 'Failed to delete touchpoint');
      }

      return response;
    },

    onSuccess: (_, touchpointId) => {
      toast({
        title: 'Touchpoint Deleted',
        description: 'Touchpoint has been successfully deleted.',
      });

      queryClient.invalidateQueries({ queryKey: touchpointQueryKeys.all });
      queryClient.removeQueries({
        queryKey: touchpointQueryKeys.detail(touchpointId),
      });
      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Delete touchpoint error:', error);

      let errorMessage = 'Failed to delete touchpoint. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;
        errorMessage = errorData.error || errorData.detail || errorMessage;
      }

      toast({
        title: 'Delete Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteSubTouchpointMutation(
  options: UseDeleteStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      subTouchpointId: string | number
    ): Promise<ItemResponse<{ id: number; deleted_at: string }>> => {
      const client = new AxiosClient<
        undefined,
        { id: number; deleted_at: string }
      >({
        endpoint: EndpointHelpers.deleteSubTouchpoint(subTouchpointId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.delete();

      if (!response.status) {
        throw new Error(response.error || 'Failed to delete sub-touchpoint');
      }

      return response;
    },

    onSuccess: (_, subTouchpointId) => {
      toast({
        title: 'Sub-Touchpoint Deleted',
        description: 'Sub-touchpoint has been successfully deleted.',
      });

      queryClient.invalidateQueries({ queryKey: subTouchpointQueryKeys.all });
      queryClient.removeQueries({
        queryKey: subTouchpointQueryKeys.detail(subTouchpointId),
      });
      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Delete sub-touchpoint error:', error);

      let errorMessage = 'Failed to delete sub-touchpoint. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;
        errorMessage = errorData.error || errorData.detail || errorMessage;
      }

      toast({
        title: 'Delete Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteTitleMutation(
  options: UseDeleteStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      titleId: string | number
    ): Promise<ItemResponse<{ id: number; deleted_at: string }>> => {
      const client = new AxiosClient<
        undefined,
        { id: number; deleted_at: string }
      >({
        endpoint: EndpointHelpers.deleteTitle(titleId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.delete();

      if (!response.status) {
        throw new Error(response.error || 'Failed to delete title');
      }

      return response;
    },

    onSuccess: (_, titleId) => {
      toast({
        title: 'Title Deleted',
        description: 'Title has been successfully deleted.',
      });

      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all });
      queryClient.removeQueries({ queryKey: titleQueryKeys.detail(titleId) });
      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Delete title error:', error);

      let errorMessage = 'Failed to delete title. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;
        errorMessage = errorData.error || errorData.detail || errorMessage;
      }

      toast({
        title: 'Delete Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

// ============================================================================
// Update Mutations
// ============================================================================

export function useUpdateDepartmentMutation(
  departmentId: string | number,
  options: UseUpdateStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      departmentData: UpdateDepartmentRequest
    ): Promise<ItemResponse<ApiDepartmentDetail>> => {
      const client = new AxiosClient<
        UpdateDepartmentRequest,
        ApiDepartmentDetail
      >({
        endpoint: EndpointHelpers.updateDepartment(departmentId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.put(departmentData);

      if (!response.status) {
        throw new Error(response.error || 'Failed to update department');
      }

      return response;
    },

    onSuccess: () => {
      toast({
        title: 'Department Updated',
        description: 'Department has been successfully updated.',
      });

      queryClient.invalidateQueries({ queryKey: departmentQueryKeys.all });
      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Update department error:', error);

      let errorMessage = 'Failed to update department. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        if (typeof errorData.detail === 'object') {
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join(', ');
        } else {
          errorMessage = errorData.error || errorData.detail || errorMessage;
        }
      }

      toast({
        title: 'Update Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateTouchpointMutation(
  touchpointId: string | number,
  options: UseUpdateStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      touchpointData: UpdateTouchpointRequest
    ): Promise<ItemResponse<ApiTouchpointDetail>> => {
      const client = new AxiosClient<
        UpdateTouchpointRequest,
        ApiTouchpointDetail
      >({
        endpoint: EndpointHelpers.updateTouchpoint(touchpointId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.put(touchpointData);

      if (!response.status) {
        throw new Error(response.error || 'Failed to update touchpoint');
      }

      return response;
    },

    onSuccess: () => {
      toast({
        title: 'Touchpoint Updated',
        description: 'Touchpoint has been successfully updated.',
      });

      queryClient.invalidateQueries({ queryKey: touchpointQueryKeys.all });
      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Update touchpoint error:', error);

      let errorMessage = 'Failed to update touchpoint. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        if (typeof errorData.detail === 'object') {
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join(', ');
        } else {
          errorMessage = errorData.error || errorData.detail || errorMessage;
        }
      }

      toast({
        title: 'Update Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateSubTouchpointMutation(
  subTouchpointId: string | number,
  options: UseUpdateStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      subTouchpointData: UpdateSubTouchpointRequest
    ): Promise<ItemResponse<ApiSubTouchpointDetail>> => {
      const client = new AxiosClient<
        UpdateSubTouchpointRequest,
        ApiSubTouchpointDetail
      >({
        endpoint: EndpointHelpers.updateSubTouchpoint(subTouchpointId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.put(subTouchpointData);

      if (!response.status) {
        throw new Error(response.error || 'Failed to update sub-touchpoint');
      }

      return response;
    },

    onSuccess: () => {
      toast({
        title: 'Sub-Touchpoint Updated',
        description: 'Sub-touchpoint has been successfully updated.',
      });

      queryClient.invalidateQueries({ queryKey: subTouchpointQueryKeys.all });
      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Update sub-touchpoint error:', error);

      let errorMessage = 'Failed to update sub-touchpoint. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        if (typeof errorData.detail === 'object') {
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join(', ');
        } else {
          errorMessage = errorData.error || errorData.detail || errorMessage;
        }
      }

      toast({
        title: 'Update Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateTitleMutation(
  titleId: string | number,
  options: UseUpdateStandardMutationOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccessCallback } = options;

  return useMutation({
    mutationFn: async (
      titleData: UpdateTitleRequest
    ): Promise<ItemResponse<ApiTitleDetail>> => {
      const client = new AxiosClient<UpdateTitleRequest, ApiTitleDetail>({
        endpoint: EndpointHelpers.updateTitle(titleId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.put(titleData);

      if (!response.status) {
        throw new Error(response.error || 'Failed to update title');
      }

      return response;
    },

    onSuccess: () => {
      toast({
        title: 'Title Updated',
        description: 'Title has been successfully updated.',
      });

      queryClient.invalidateQueries({ queryKey: titleQueryKeys.all });
      onSuccessCallback?.();
    },

    onError: (error: AxiosError) => {
      console.error('Update title error:', error);

      let errorMessage = 'Failed to update title. Please try again.';

      if (error.response?.data) {
        const errorData = error.response.data as ItemResponse<unknown>;

        if (typeof errorData.detail === 'object') {
          const validationErrors = Object.values(errorData.detail).flat();
          errorMessage = validationErrors.join(', ');
        } else {
          errorMessage = errorData.error || errorData.detail || errorMessage;
        }
      }

      toast({
        title: 'Update Failed',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
}

// ============================================================================
// Infinite Scroll Queries
// ============================================================================

export function useInfiniteDepartmentsQuery(
  propertyTypeId: string | number,
  params: Omit<DepartmentsListParams, 'page'> = {},
  enabled = true
) {
  return useInfiniteQuery({
    queryKey: [
      ...departmentQueryKeys.lists(),
      propertyTypeId,
      'infinite',
      params,
    ],
    queryFn: async ({
      pageParam = 1,
    }): Promise<ListPaginated<ApiDepartment>> => {
      const client = new AxiosClient<undefined, ApiDepartment>({
        endpoint: EndpointHelpers.getDepartmentsList(propertyTypeId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getPaginated({
        params: {
          page: pageParam,
          page_size: params.page_size || DEFAULT_PAGE_SIZE,
          ...(params.search && { search: params.search }),
        },
      });

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch departments');
      }

      return response;
    },
    getNextPageParam: lastPage => {
      if (
        lastPage.pagination.has_more_pages &&
        lastPage.pagination.current_page
      ) {
        return lastPage.pagination.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!propertyTypeId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInfiniteTouchpointsQuery(
  propertyTypeId: string | number,
  params: Omit<TouchpointsListParams, 'page'> = {},
  enabled = true
) {
  return useInfiniteQuery({
    queryKey: [
      ...touchpointQueryKeys.lists(),
      propertyTypeId,
      'infinite',
      params,
    ],
    queryFn: async ({
      pageParam = 1,
    }): Promise<ListPaginated<ApiTouchpoint>> => {
      const client = new AxiosClient<undefined, ApiTouchpoint>({
        endpoint: EndpointHelpers.getTouchpointsList(propertyTypeId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getPaginated({
        params: {
          page: pageParam,
          page_size: params.page_size || 1000,
          ...(params.search && { search: params.search }),
        },
      });

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch touchpoints');
      }

      return response;
    },
    getNextPageParam: lastPage => {
      if (
        lastPage.pagination.has_more_pages &&
        lastPage.pagination.current_page
      ) {
        return lastPage.pagination.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!propertyTypeId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInfiniteSubTouchpointsQuery(
  propertyTypeId: string | number,
  params: Omit<SubTouchpointsListParams, 'page'> = {},
  enabled = true
) {
  return useInfiniteQuery({
    queryKey: [
      ...subTouchpointQueryKeys.lists(),
      propertyTypeId,
      'infinite',
      params,
    ],
    queryFn: async ({
      pageParam = 1,
    }): Promise<ListPaginated<ApiSubTouchpoint>> => {
      const client = new AxiosClient<undefined, ApiSubTouchpoint>({
        endpoint: EndpointHelpers.getSubTouchpointsList(propertyTypeId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getPaginated({
        params: {
          page: pageParam,
          page_size: params.page_size || 1000,
          ...(params.search && { search: params.search }),
        },
      });

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch sub-touchpoints');
      }

      return response;
    },
    getNextPageParam: lastPage => {
      if (
        lastPage.pagination.has_more_pages &&
        lastPage.pagination.current_page
      ) {
        return lastPage.pagination.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!propertyTypeId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInfiniteTitlesQuery(
  propertyTypeId: string | number,
  params: Omit<TitlesListParams, 'page'> = {},
  enabled = true
) {
  return useInfiniteQuery({
    queryKey: [...titleQueryKeys.lists(), propertyTypeId, 'infinite', params],
    queryFn: async ({ pageParam = 1 }): Promise<ListPaginated<ApiTitle>> => {
      const client = new AxiosClient<undefined, ApiTitle>({
        endpoint: EndpointHelpers.getTitlesList(propertyTypeId),
        contentType: 'application/json',
        isToken: true,
      });

      const response = await client.getPaginated({
        params: {
          page: pageParam,
          page_size: params.page_size || 1000,
          ...(params.search && { search: params.search }),
        },
      });

      if (!response.status) {
        throw new Error(response.error || 'Failed to fetch titles');
      }

      return response;
    },
    getNextPageParam: lastPage => {
      if (
        lastPage.pagination.has_more_pages &&
        lastPage.pagination.current_page
      ) {
        return lastPage.pagination.current_page + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    enabled: !!propertyTypeId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}
